import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { AdminHeader, Card, Btn, Input } from "@/components/admin/ui";
import { toast } from "sonner";
import { ShieldCheck, User, Lock, CheckCircle2 } from "lucide-react";

export default function AdminUsers() {
  const [account, setAccount] = useState(null);
  const [pw, setPw] = useState({ currentPassword: "", newPassword: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/account").then(({ data }) => setAccount(data.account)); }, []);

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.newPassword.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (pw.newPassword !== pw.confirm) { toast.error("Passwords do not match"); return; }
    setSaving(true);
    try {
      await api.post("/admin/account/change-password", { currentPassword: pw.currentPassword, newPassword: pw.newPassword });
      toast.success("Password updated");
      setPw({ currentPassword: "", newPassword: "", confirm: "" });
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <AdminHeader title="Account & Security" subtitle="Single-administrator account for this store" />
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-5">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2"><User className="h-5 w-5 text-slate-500" /> Administrator</h3>
          {account && (
            <div className="space-y-3 text-sm">
              <Row label="Name" value={account.name} />
              <Row label="Email" value={account.email} />
              <Row label="Role" value={<span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 uppercase">{account.role}</span>} />
              <Row label="Two-step verification" value={account.mfaEnabled
                ? <span className="inline-flex items-center gap-1 text-emerald-600 font-semibold"><ShieldCheck className="h-4 w-4" /> Enabled</span>
                : <span className="text-amber-600 font-semibold">Set up on next login</span>} />
            </div>
          )}
          <p className="text-xs text-slate-500 mt-4 p-3 bg-slate-50 border border-slate-200 rounded-lg">This store uses a single-administrator model. Only this email may access the admin portal, enforced server-side. There is no multi-admin management.</p>
        </Card>

        <Card className="p-5">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2"><Lock className="h-5 w-5 text-slate-500" /> Change Password</h3>
          <form onSubmit={changePassword} className="space-y-4">
            <Input label="Current Password" type="password" value={pw.currentPassword} onChange={(e) => setPw({ ...pw, currentPassword: e.target.value })} testid="current-password" />
            <Input label="New Password" type="password" value={pw.newPassword} onChange={(e) => setPw({ ...pw, newPassword: e.target.value })} testid="new-password" />
            <Input label="Confirm New Password" type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} testid="confirm-password" />
            <Btn variant="accent" type="submit" loading={saving} data-testid="change-password-btn"><CheckCircle2 className="h-4 w-4" /> Update Password</Btn>
          </form>
        </Card>
      </div>
    </div>
  );
}

const Row = ({ label, value }) => <div className="flex items-center justify-between"><span className="text-slate-500">{label}</span><span className="text-slate-900 font-medium">{value}</span></div>;
